import React, { useState } from 'react';
import { X, ChevronRight } from 'lucide-react';

import CaregiverSetupQR from './CaregiverSetupQR';

/**
 * Caregiver setup-credentials success modal (FECOA-1428/1429/1430).
 *
 * Shown after one or more caregivers are created with an in-clinic temporary
 * PIN, so the SC can share each caregiver's setup QR / ID / PIN during the
 * onboarding session.
 *
 *  - 1 caregiver  → single centered card (FECOA-1429 / 1430)
 *  - 2+ caregivers → left selector list + selected caregiver's QR (FECOA-1428)
 *
 * Privacy (FECOA-1428 note): the multi-caregiver selector masks other
 * caregivers to "CG-ID · Initials" so a caregiver facing the device can't read
 * another's full legal name. The selected caregiver's full name still shows on
 * their own card, and "Enlarge for Scanning" hides everything but the QR.
 *
 * Props:
 *   - caregivers: [{ caregiverId, tempPin, name, relationship }]
 *   - studyCode: the participant's Study Code, which caregivers sign in with
 *     (FECOA-2156)
 *   - sponsorId: only used for the setup link when the study code is unknown
 *   - onClose
 */
const initialsOf = (name) => {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean)
    .join('.')
    .concat('.');
};

const CaregiverSetupModal = ({ caregivers = [], studyCode, sponsorId, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMulti = caregivers.length > 1;
  const selected = caregivers[selectedIndex] || caregivers[0];

  if (!caregivers.length) return null;

  const card = selected && (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-900/40">
      <CaregiverSetupQR
        caregiverId={selected.caregiverId}
        tempPin={selected.tempPin}
        studyCode={studyCode}
        sponsorId={sponsorId}
        name={selected.name}
        relationship={selected.relationship}
      />
    </div>
  );

  const instructions = (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
      <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
        <strong className="text-gray-900 dark:text-white">Instructions:</strong>{' '}
        {studyCode
          ? 'The caregiver can scan this QR code, or sign in on the flexCOA app or website with the Study Code, Caregiver ID and Temporary PIN to complete their setup.'
          : 'The caregiver can scan this QR code or manually enter the Caregiver ID and Temporary PIN to complete their setup.'}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="flex max-h-[92vh] w-full max-w-2xl animate-slide-up flex-col overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        {/* Header */}
        <div className="card-band flex-shrink-0 flex-nowrap">
          <h2 className="card-band-title">
            {isMulti ? 'Caregivers Setup Ready' : 'Caregiver Setup Ready'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isMulti ? (
            <div className="grid grid-cols-1 sm:grid-cols-3">
              {/* Left: masked selector list (FECOA-1428 privacy) */}
              <div className="sm:col-span-1 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700 p-4">
                <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {caregivers.length} Caregivers Created
                </p>
                <div className="space-y-2">
                  {caregivers.map((cg, index) => {
                    const isActive = index === selectedIndex;
                    return (
                      <button
                        key={cg.caregiverId}
                        type="button"
                        onClick={() => setSelectedIndex(index)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${
                          isActive
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-gray-200 dark:hover:bg-white/10'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-mono font-semibold truncate">
                            {cg.caregiverId}
                          </p>
                          <p
                            className={`text-xs truncate ${
                              isActive ? 'text-primary-50' : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {initialsOf(cg.name) || '—'}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-70" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: selected caregiver detail */}
              <div className="sm:col-span-2 p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                  {caregivers.length} caregiver accounts created successfully. Share each
                  caregiver&rsquo;s setup information below.
                </p>
                {card}
                {instructions}
              </div>
            </div>
          ) : (
            <div className="p-5">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                Caregiver account created successfully. Share this information with the caregiver to
                complete setup.
              </p>
              {card}
              {instructions}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50/70 px-5 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-primary w-full"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaregiverSetupModal;
